// @flow

import DOM from '../../util/dom';
import {extend, bindAll} from '../../util/util';
import {Event} from '../../util/evented';
import type Map from '../map';
import {ContentVisibilityEvent, ContentRemoveEvent} from '../events';

export type Options = {
    id: string,
    loadingEventId: string,
    isVisible: ?boolean
};

export class FilterControlBase {
    _map: Map;
    _options: Options;
    _container: HTMLElement;
    _filterObjects: Array<Object>;
    _selectElements: Array<any>;
    _filterTitle: string;
    _layers: Array<string>;
    _containerClass: string;
    _headerClass: string;
    parentNode: Object;

    constructor(options: Options) {
        this._containerClass = 'trimblemaps-ctrl-filter-container';
        this._headerClass = 'trimblemaps-ctrl-accordion';
        this._options = extend({}, options);
        this._selectElements = [];
        bindAll([
            '_getFilter',
            'filter',
            '_onChange',
            '_createElement',
            '_contentLayerLoaded',
            '_contentLayerVisibilityChanged',
            '_contentLayerRemoved',
        ], this);
    }

    _getFilter() {
        const filter = [];
        let items: Array<any>;
        if (this._options.isVisible) {
            items = this._selectElements;
        } else {
            items = this._filterObjects;
        }
        items.forEach(item => {
            const key = item.id;
            const value = item.value || item.default;
            const f = [];
            if (value !== 'All') {
                f.push('==');
                f.push(key);
                f.push(value);
                filter.push(f);
            }
        });
        return filter;
    }

    filter() {
        let filter = this._getFilter();
        filter = ['all', ...filter];

        if (this._layers) {
            this._layers.forEach(layerName => {
                if (this._map.getLayer(layerName)) {
                    this._map.setFilter(layerName, filter);
                }
            });
        }
    }

    _onChange() {
        const keyValuePairs = [];
        this._selectElements.forEach(select => {
            const id = select.id;
            const value = select.value;
            keyValuePairs.push({
                id,
                value
            });
        });
        this.filter();

        /**
         * Fired when change content layer filters.
         *
         * @event content.filter
         * @memberof Map
         * @instance
         * @type {Object}
         * @property {Event} object with control Id and selected key value pairs have been returned.
         */
        this._map.fire(new Event(
            'content.filter',
            { // do we need to define type for event data?
                id: this._options.id,
                data: keyValuePairs
            }
        ));
    }

    _createElement(options: any) {
        const div = DOM.create('div', 'trimblemaps-ctrl-accordion-content trimblemaps-ctrl-trfilter');
        //div.setAttribute("id", options.id);
        const label = DOM.create('label');
        const span = DOM.create('span');
        span.innerHTML = options.label;
        const select = DOM.create('select');
        select.id = `${options.id}`;
        options.values.forEach((e, i) => {
            const option = DOM.create('option');
            option.value = e;
            option.innerHTML = options.texts[i];
            select.appendChild(option);
        });
        select.addEventListener('change', this._onChange);
        this._selectElements.push(select);
        select.value = options.default;
        label.appendChild(span);
        label.appendChild(select);
        div.appendChild(label);
        return div;
    }

    onAdd(map: Map) {
        this._map = map;
        this._container = DOM.create('div', 'trimblemaps-ctrl');
        this._container.classList.add(this._containerClass);
        const header = DOM.create('div', this._headerClass);
        this._container.setAttribute("id", this._options.id);
        const btn = DOM.create('button', 'trimblemaps-ctrl-accordion-header');
        btn.innerHTML = this._filterTitle;
        header.appendChild(btn);
        header.addEventListener('click', this._toggleMenu);
        this._container.appendChild(header);
        this._container.style.display = 'none';
        this._filterObjects.forEach((element) => {
            this._container.appendChild(this._createElement(element));
        });

        // if layer is already loaded, match the visibility
        if (this._map.getLayer(this._layers[0])) {
            const layerVisibility = this._map.getLayoutProperty(this._layers[0], 'visibility');
            this._container.style.display = (layerVisibility === 'visible' && this._options.isVisible) ? 'block' : 'none';
        }
        // $FlowFixMe
        this._map.on(this._options.loadingEventId, this._contentLayerLoaded);
        this._map.on('content.visibility', this._contentLayerVisibilityChanged);
        this._map.on('content.remove', this._contentLayerRemoved);
        return this._container;
    }

    _toggleMenu() {
        let index;
        // eslint-disable-next-line no-undef
        const layerContentId =  document.getElementById(this.parentNode.id);
        if (layerContentId && layerContentId.hasChildNodes) {
            for (index = 0; index < layerContentId.children.length; index++) {
                if (layerContentId.children[index].className === 'trimblemaps-ctrl-accordion') {
                    layerContentId.children[index].children[index].classList.toggle('active');
                } else {
                    layerContentId.children[index].classList.toggle('active');
                }
            }
        }
    }

    onRemove() {
        // $FlowFixMe
        this._map.off(this._options.loadingEventId, this._contentLayerLoaded);
        this._map.off('content.visibility', this._contentLayerVisibilityChanged);
        this._map.off('content.remove', this._contentLayerRemoved);
        this._clearFilter();
        DOM.remove(this._container);
        delete this._map;
    }

    _clearFilter() {
        if (this._layers) {
            this._layers.forEach(layerName => {
                if (this._map.getLayer(layerName)) {
                    this._map.setFilter(layerName, null);
                }
            });
        }
    }

    _contentLayerLoaded() {
        this.filter();
    }

    _contentLayerVisibilityChanged(evt: ContentVisibilityEvent) {
        if (evt.id === this._options.id) {
            this._container.style.display = (evt.isVisible && this._options.isVisible) ? 'block' : 'none';
        }
    }

    _contentLayerRemoved(evt: ContentRemoveEvent) {
        if (evt.id === this._options.id) {
            this._container.style.display = 'none';
        }
    }

}
